import { roomLib } from "@agentcall/core/lib"
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query"
import { format } from "date-fns"
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { Check, File as FileIcon, Link, Loader, Paperclip, Send, Trash } from "react-feather"
import { toast } from "sonner"
import { UPLOAD_ACCEPT, uploadFile } from "@/api/client/file"
import { roomApi } from "@/api/client/room"
import { ticketApi } from "@/api/client/ticket"
import type { TicketMessageList200TicketMessagesItem } from "@/api/generated/models"
import { RoomGetWithPlans } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { cn } from "@/lib/utils"

// ファイルタイプ判定ヘルパー
const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

// URLを検出してリンク化する関数
const URL_REGEX = /(https?:\/\/[^\s]+)/g
// Markdown形式 [テキスト](URL) のリンクを検出する正規表現
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g

function linkifyText(text: string, className?: string): React.ReactNode[] {
  const result: React.ReactNode[] = []
  let lastIndex = 0
  let keyIndex = 0

  // まずMarkdown形式のリンクを処理
  const markdownMatches = [...text.matchAll(MARKDOWN_LINK_REGEX)]

  for (const match of markdownMatches) {
    const [fullMatch, linkText, url] = match
    const matchIndex = match?.index ?? 0

    // マッチ前のテキストを処理（通常のURL検出）
    if (matchIndex > lastIndex) {
      const beforeText = text.substring(lastIndex, matchIndex)
      result.push(...linkifyPlainUrls(beforeText, className, keyIndex))
      keyIndex += beforeText.length
    }

    // Markdownリンクを追加
    result.push(
      <a
        key={`md-${keyIndex++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={cn("underline hover:opacity-80", className)}
      >
        {linkText}
      </a>
    )

    lastIndex = matchIndex + fullMatch.length
  }

  // 残りのテキストを処理
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex)
    result.push(...linkifyPlainUrls(remainingText, className, keyIndex))
  }

  return result.length > 0 ? result : [text]
}

// 通常のURLのみをリンク化する補助関数
function linkifyPlainUrls(text: string, className?: string, startKey = 0): React.ReactNode[] {
  const parts = text.split(URL_REGEX)
  return parts
    .map((part) => {
      if (URL_REGEX.test(part)) {
        URL_REGEX.lastIndex = 0
        return (
          <a
            key={`url-${startKey}-${part}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className={cn("underline hover:opacity-80", className)}
          >
            {part}
          </a>
        )
      }
      return part ? <Fragment key={`text-${startKey}-${part}`}>{part}</Fragment> : null
    })
    .filter(Boolean) as React.ReactNode[]
}

// 改行とURLリンクを処理してレンダリング
function renderMessageContent(message: string, linkClassName?: string) {
  const lines = message.split("\n")
  return lines.map((line, lineIndex) => (
    <Fragment key={line}>
      {lineIndex > 0 && <br />}
      {linkifyText(line, linkClassName)}
    </Fragment>
  ))
}

interface ContactInfoProps {
  ticketId: string
  ownerName?: string
  isMessageReadByAdmin?: boolean
  propertyId?: number | null
  roomId?: number | null
  onTransferMessageToProgress?: (message: TicketMessageList200TicketMessagesItem) => void
}

const MESSAGES_PER_PAGE = 20

export function ContactInfo({
  ticketId,
  ownerName = "山田太郎",
  isMessageReadByAdmin,
  propertyId,
  roomId,
  onTransferMessageToProgress,
}: ContactInfoProps) {
  const queryClient = useQueryClient()
  const { canUpdate } = usePermission()
  const canEditTicket = canUpdate("tickets")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const messagePointerStartRef = useRef<{ x: number; y: number } | null>(null)
  const [messageText, setMessageText] = useState("")

  // スクロール位置維持用
  const previousScrollHeightRef = useRef<number>(0)
  const shouldScrollToBottomRef = useRef(true)

  // 最新メッセージID追跡用（オーナー新着検知）
  const latestMessageIdRef = useRef<string | number | null>(null)

  // テキスト選択状態を追跡
  const [selection, setSelection] = useState<{ start: number; end: number }>({ start: 0, end: 0 })

  // 既読処理完了状態
  const [isReadComplete, setIsReadComplete] = useState(isMessageReadByAdmin)

  // ファイルプレビュー用のstate
  const [previewFile, setPreviewFile] = useState<{
    file: File
    url: string
    isImage: boolean
  } | null>(null)

  // ファイルアップロード中の状態
  const [isUploading, setIsUploading] = useState(false)
  const [isFileDragging, setIsFileDragging] = useState(false)

  // 削除確認ダイアログ用のstate
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargetMessageId, setDeleteTargetMessageId] = useState<string | null>(null)

  // Room/Plan データ取得（解約リンク挿入用）
  const { data: roomData } = useQuery({
    queryKey: ["room", roomId],
    queryFn: () => roomApi.roomGet(String(roomId), { withPlans: RoomGetWithPlans.true }),
    enabled: !!roomId,
  })
  const currentPlan = roomData?.room ? roomLib.roomCurrentPlan(roomData.room) : null

  // isMessageReadByAdmin props変更に追従
  useEffect(() => {
    setIsReadComplete(isMessageReadByAdmin ?? false)
  }, [isMessageReadByAdmin])

  // useInfiniteQuery でメッセージを取得
  // sort=desc (デフォルト) で新しい順に取得
  // page 1 = 最新メッセージ, page 2 = より古いメッセージ
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["ticketMessages", ticketId],
    queryFn: async ({ pageParam }) => {
      return ticketApi.ticketMessageList(ticketId, {
        page: String(pageParam),
        limit: String(MESSAGES_PER_PAGE),
        sort: "desc",
        withAdmin: "true",
        withOwner: "true",
        withFiles: "true",
      })
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, page) => sum + page.ticketMessages.length, 0)
      return loadedCount < lastPage.total ? allPages.length + 1 : undefined
    },
  })

  // 全ページのメッセージを結合して表示順に並べる
  // APIはDESC（新しい順）で返すので:
  // - 各ページ内を逆順にして古い順に
  // - ページ配列を逆順にして古いページを先に
  const messages =
    data?.pages
      .slice()
      .reverse()
      .flatMap((page) => [...page.ticketMessages].reverse()) ?? []

  // オーナーからの新着メッセージを検知してチェックを外す
  useEffect(() => {
    if (!messages.length) return
    const latestMessage = messages[messages.length - 1]
    if (latestMessage && latestMessage.id !== latestMessageIdRef.current) {
      if (
        latestMessageIdRef.current !== null &&
        latestMessage.senderType !== "admin" &&
        !latestMessage.deletedAt
      ) {
        setIsReadComplete(false)
      }
      latestMessageIdRef.current = latestMessage.id
    }
  }, [messages])

  // 一番下までスクロールする関数
  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
    }
  }, [])

  // 古いメッセージを読み込む前にスクロール位置を記録
  const handleLoadMore = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return
    previousScrollHeightRef.current = scrollContainerRef.current?.scrollHeight ?? 0
    shouldScrollToBottomRef.current = false
    fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // スクロールイベントハンドラ
  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    // スクロール位置が上端に近い場合（20px以内）、さらにメッセージを読み込む
    if (scrollContainer.scrollTop < 20 && hasNextPage && !isFetchingNextPage) {
      handleLoadMore()
    }
  }, [hasNextPage, isFetchingNextPage, handleLoadMore])

  // 初期読み込み後に一番下までスクロール
  useLayoutEffect(() => {
    if (data && shouldScrollToBottomRef.current) {
      scrollToBottom()
    }
  }, [data, scrollToBottom])

  // 古いメッセージ読み込み後にスクロール位置を維持
  useLayoutEffect(() => {
    if (!isFetchingNextPage && previousScrollHeightRef.current > 0) {
      const scrollContainer = scrollContainerRef.current
      if (scrollContainer) {
        const newScrollHeight = scrollContainer.scrollHeight
        scrollContainer.scrollTop = newScrollHeight - previousScrollHeightRef.current
      }
      previousScrollHeightRef.current = 0
    }
  }, [isFetchingNextPage])

  // 定期的なデータ更新（最新のメッセージを取得）
  useEffect(() => {
    const interval = setInterval(() => {
      // キャッシュを無効化して最新データを取得
      queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
    }, 10000)

    return () => clearInterval(interval)
  }, [ticketId, queryClient])

  const createMutation = ticketApi.useTicketMessageCreate({
    mutation: {
      onSuccess: () => {
        setMessageText("")
        shouldScrollToBottomRef.current = true
        // キャッシュを無効化して最新データを取得
        queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
      },
    },
  })

  const deleteMutation = ticketApi.useTicketMessageDelete({
    mutation: {
      onSuccess: () => {
        // キャッシュを無効化
        queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
        // ダイアログを閉じる
        setDeleteDialogOpen(false)
        setDeleteTargetMessageId(null)
      },
      onError: (error) => {
        toast.error("メッセージの削除に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const readMessageMutation = ticketApi.useTicketReadMessage({
    mutation: {
      onSuccess: (_data, variables) => {
        setIsReadComplete(variables.data.isRead)
        // キャッシュを無効化
        queryClient.invalidateQueries({ queryKey: ["ticketMessages", ticketId] })
      },
    },
  })

  const handleSendMessage = () => {
    if (!messageText.trim()) return
    createMutation.mutate({
      ticketId,
      data: { message: messageText },
    })
  }

  const handleDeleteClick = (ticketMessageId: string) => {
    setDeleteTargetMessageId(ticketMessageId)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (deleteTargetMessageId) {
      deleteMutation.mutate({ ticketId, ticketMessageId: deleteTargetMessageId })
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setDeleteTargetMessageId(null)
  }

  const handleReadMessage = () => {
    readMessageMutation.mutate({
      id: ticketId,
      data: { isRead: !isReadComplete },
    })
  }

  // Shift+Enterで送信
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  const openFilePreview = (file: File) => {
    if (file) {
      const isImage = file.type.startsWith("image/")
      const url = isImage ? URL.createObjectURL(file) : ""
      setPreviewFile({ file, url, isImage })
    }
  }

  // ファイル選択時にプレビューダイアログを表示
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      openFilePreview(file)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsFileDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) {
      openFilePreview(file)
    }
  }

  const handleFileDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (event.dataTransfer.types.includes("Files")) {
      setIsFileDragging(true)
    }
  }

  const handleFileDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const relatedTarget = event.relatedTarget
    if (relatedTarget instanceof Node && event.currentTarget.contains(relatedTarget)) {
      return
    }
    setIsFileDragging(false)
  }

  // ファイルプレビューダイアログを閉じる
  const handleClosePreview = () => {
    if (previewFile?.url) {
      URL.revokeObjectURL(previewFile.url)
    }
    setPreviewFile(null)
  }

  // ファイルを送信
  const handleSendFile = async () => {
    if (!previewFile) return

    setIsUploading(true)
    try {
      // S3にファイルをアップロード
      const uploadResult = await uploadFile(previewFile.file)

      // メッセージを作成（ファイル添付）
      createMutation.mutate({
        ticketId,
        data: {
          message: previewFile.file.name,
          uploadFile: {
            path: uploadResult.key,
            fileName: uploadResult.fileName,
          },
        },
      })

      handleClosePreview()
    } catch (error) {
      console.error("File upload failed:", error)
      alert("ファイルのアップロードに失敗しました")
    } finally {
      setIsUploading(false)
    }
  }

  const handleAttachClick = () => {
    fileInputRef.current?.click()
  }

  const copyMessageText = async (message: string) => {
    if (!navigator.clipboard?.writeText) {
      toast.info("本文を進捗追加へ反映しました。クリップボードへのコピーは利用できませんでした。")
      return
    }

    try {
      await navigator.clipboard.writeText(message)
      toast.success("本文をコピーし、進捗追加へ反映しました")
    } catch {
      toast.info("本文を進捗追加へ反映しました。クリップボードへのコピーは失敗しました。")
    }
  }

  const handleMessagePointerDown = (event: React.PointerEvent) => {
    messagePointerStartRef.current = { x: event.clientX, y: event.clientY }
  }

  const handleMessageTransfer = async (
    event: React.MouseEvent | React.KeyboardEvent,
    message: TicketMessageList200TicketMessagesItem
  ) => {
    if (!canEditTicket || !onTransferMessageToProgress) return
    if (message.deletedAt || !message.message.trim()) return
    if (event.target instanceof Element && event.target.closest("a")) return
    if (window.getSelection()?.toString()) return

    if ("clientX" in event && messagePointerStartRef.current) {
      const movedX = Math.abs(event.clientX - messagePointerStartRef.current.x)
      const movedY = Math.abs(event.clientY - messagePointerStartRef.current.y)
      if (movedX > 8 || movedY > 8) return
    }

    await copyMessageText(message.message)
    onTransferMessageToProgress(message)
  }

  // テキスト選択状態を更新
  const handleSelectionChange = () => {
    const textarea = textareaRef.current
    if (textarea) {
      setSelection({ start: textarea.selectionStart, end: textarea.selectionEnd })
    }
  }

  // 解約リンクを挿入
  const handleInsertCancelLink = () => {
    if (!currentPlan || !propertyId || !roomId) return
    const feOwnerUrl = import.meta.env.VITE_FE_OWNER_URL || "http://localhost:5179"
    const cancelUrl = `${feOwnerUrl}/properties/${propertyId}/rooms/${roomId}/plans/${currentPlan.id}/cancel`
    const { start } = selection
    const newText = messageText.substring(0, start) + cancelUrl + messageText.substring(start)
    setMessageText(newText)
  }

  const formatMessageType = (
    message: TicketMessageList200TicketMessagesItem
  ): "sent" | "received" | "system" | "deleted" => {
    if (message.deletedAt) return "deleted"
    if ((message.senderType as string) === "system") return "system"
    return message.senderType === "admin" ? "sent" : "received"
  }

  return (
    <Card className="grid h-full min-h-0 grid-cols-1 grid-rows-[auto_minmax(0,1fr)_auto] p-0">
      <div className="bg-primary rounded-t-lg border-b px-4 py-2 text-center">
        <span className="text-sm text-white font-semibold">(オーナー){ownerName}さんとの連絡</span>
      </div>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 overscroll-contain overflow-auto p-4"
      >
        <div className="space-y-4">
          {(isPending || isFetchingNextPage) && (
            <div className="flex justify-center py-2">
              <Loader size={16} className="animate-spin text-slate-400" />
            </div>
          )}
          {!isPending && messages.length === 0 && !isFetchingNextPage && (
            <p className="text-sm text-slate-500">メッセージがありません</p>
          )}
          {messages.map((message) => {
            const messageType = formatMessageType(message)
            const timestamp = format(new Date(message.createdAt), "yyyy-MM-dd HH:mm")

            if (messageType === "deleted") {
              return (
                <div key={message.id} className="flex justify-center">
                  <div className="bg-slate-50 px-4 py-1 rounded-full">
                    <p className="text-xs text-[#354267] font-medium">メッセージを削除しました</p>
                  </div>
                </div>
              )
            }

            if (messageType === "system") {
              return (
                <div key={message.id} className="flex justify-center">
                  <div className="max-w-[320px] rounded-md border border-slate-200 bg-slate-50 p-2">
                    <div className="flex items-center justify-between gap-2.5 mb-2">
                      <span className="text-xs font-semibold text-slate-700">システム通知</span>
                      <span className="text-xs font-semibold text-slate-400">{timestamp}</span>
                    </div>
                    <p className="text-xs leading-4 whitespace-pre-wrap break-words text-slate-700">
                      {renderMessageContent(message.message, "text-blue-600")}
                    </p>
                  </div>
                </div>
              )
            }

            return (
              <div
                key={message.id}
                className={cn("flex", messageType === "sent" ? "justify-end" : "justify-start")}
              >
                <div className="max-w-[255px]">
                  <div className="flex items-end gap-1">
                    {messageType === "sent" && canEditTicket && (
                      <Trash
                        size={14}
                        className="text-[#354267] mb-1.5 cursor-pointer hover:text-gray-600"
                        onClick={() => handleDeleteClick(String(message.id))}
                      />
                    )}
                    <div
                      className={cn(
                        "rounded-md p-2 flex-1",
                        messageType === "sent" ? "bg-[#ebf0f6]" : "bg-primary text-white"
                      )}
                    >
                      {/* ファイル表示 */}
                      {message.files && message.files.length > 0 && (
                        <div className="mb-2 space-y-2">
                          {message.files.map((file) => (
                            <div key={file.id}>
                              {isImageFile(file.fileType, file.fileName) ? (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <img
                                    src={file.url}
                                    alt={file.fileName || "添付画像"}
                                    className="max-w-full h-auto max-h-[150px] object-contain rounded-md"
                                  />
                                </a>
                              ) : (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    "flex items-center gap-2 p-2 rounded-md",
                                    messageType === "sent"
                                      ? "bg-slate-200 hover:bg-slate-300"
                                      : "bg-white/10 hover:bg-white/20"
                                  )}
                                >
                                  <FileIcon
                                    size={14}
                                    className={
                                      messageType === "sent" ? "text-slate-600" : "text-white"
                                    }
                                  />
                                  <span
                                    className={cn(
                                      "text-xs underline truncate",
                                      messageType === "sent" ? "text-slate-900" : "text-white"
                                    )}
                                  >
                                    {file.fileName || "添付ファイル"}
                                  </span>
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      <p
                        className={cn(
                          "text-xs leading-4 whitespace-pre-wrap break-words",
                          canEditTicket &&
                            onTransferMessageToProgress &&
                            message.message.trim() &&
                            "cursor-copy rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#458CF7]"
                        )}
                        role={
                          canEditTicket && onTransferMessageToProgress && message.message.trim()
                            ? "button"
                            : undefined
                        }
                        tabIndex={
                          canEditTicket && onTransferMessageToProgress && message.message.trim()
                            ? 0
                            : undefined
                        }
                        title={
                          canEditTicket && onTransferMessageToProgress && message.message.trim()
                            ? "本文をコピーして進捗追加へ反映"
                            : undefined
                        }
                        onPointerDown={handleMessagePointerDown}
                        onClick={(event) => handleMessageTransfer(event, message)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault()
                            handleMessageTransfer(event, message)
                          }
                        }}
                      >
                        {renderMessageContent(
                          message.message,
                          messageType === "sent" ? "text-blue-600" : "text-blue-200"
                        )}
                      </p>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "text-xs mt-1",
                      messageType === "sent" ? "text-left" : "text-right"
                    )}
                  >
                    {messageType === "received" && message.readAt && (
                      <span className="text-[#354267] text-xs font-semibold mr-2">既読</span>
                    )}
                    <span className="text-[#9eafc7]">{timestamp}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
      {canEditTicket ? (
        <div
          onDrop={handleFileDrop}
          onDragOver={handleFileDragOver}
          onDragLeave={handleFileDragLeave}
          className={cn(
            "px-4 py-2.5 border-t bg-white rounded-b-lg shadow-[0px_-2px_8px_0px_rgba(19,46,90,0.05)] transition-colors",
            isFileDragging && "bg-blue-50 ring-2 ring-inset ring-blue-300"
          )}
        >
          <div className="space-y-2.5">
            {isFileDragging && (
              <div className="rounded-md border border-dashed border-blue-300 bg-white px-3 py-2 text-center text-xs font-semibold text-blue-600">
                添付するファイルをここにドロップ
              </div>
            )}
            <Textarea
              ref={textareaRef}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={handleKeyDown}
              onSelect={handleSelectionChange}
              onFocus={handleSelectionChange}
              onClick={handleSelectionChange}
              placeholder="オーナー様へメッセージを入力（Shift+Enterで送信）"
              className="min-h-[56px] resize-y border-slate-300 rounded-md text-sm placeholder:text-slate-300"
            />
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={UPLOAD_ACCEPT}
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-8 h-8 p-0 border-slate-300 rounded-md"
                  onClick={handleAttachClick}
                >
                  <Paperclip size={14} className="text-[#354267]" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-8 h-8 p-0 border-slate-300 rounded-md"
                  onClick={handleInsertCancelLink}
                  disabled={!currentPlan || !propertyId || !roomId}
                >
                  <Link size={14} className="text-[#354267]" />
                </Button>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "w-8 h-8 p-0 border-slate-300 rounded-md",
                    isReadComplete && "bg-emerald-600 hover:bg-emerald-400"
                  )}
                  onClick={handleReadMessage}
                  disabled={readMessageMutation.isPending}
                >
                  <Check size={14} className={cn(isReadComplete && "text-white")} />
                </Button>
                <Button
                  size="sm"
                  className="h-8 px-4 gap-2 bg-[#354267] hover:bg-[#354267]/90 text-white font-bold text-sm"
                  onClick={handleSendMessage}
                  disabled={createMutation.isPending || !messageText.trim()}
                >
                  <Send size={14} />
                  送信
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2.5 border-t bg-slate-50 rounded-b-lg">
          <p className="text-sm text-slate-400 text-center">メッセージの閲覧のみ可能です</p>
        </div>
      )}

      {/* ファイルプレビューダイアログ */}
      <Dialog open={!!previewFile} onOpenChange={(open) => !open && handleClosePreview()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{previewFile?.isImage ? "画像プレビュー" : "ファイル確認"}</DialogTitle>
          </DialogHeader>
          {previewFile && (
            <div className="relative">
              {previewFile.isImage ? (
                <img
                  src={previewFile.url}
                  alt="プレビュー"
                  className="w-full h-auto max-h-[400px] object-contain rounded-md"
                />
              ) : (
                <div className="flex items-center gap-3 p-4 bg-slate-100 rounded-md">
                  <FileIcon size={24} className="text-slate-600" />
                  <span className="text-sm text-slate-900 truncate">{previewFile.file.name}</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleClosePreview} disabled={isUploading}>
              キャンセル
            </Button>
            <Button onClick={handleSendFile} disabled={isUploading}>
              {isUploading ? (
                <>
                  <Loader size={14} className="animate-spin mr-2" />
                  アップロード中...
                </>
              ) : (
                "送信"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleDeleteCancel()
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>本当に削除してよろしいですか？</DialogTitle>
            <DialogDescription>削除したメッセージは復元できません。</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDeleteCancel}
              disabled={deleteMutation.isPending}
            >
              キャンセル
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "削除中..." : "削除する"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
